import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import { task, type TaskInstance } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import type { Demand } from 'lumen/models';
import type { Day } from 'lumen/services/repositories/day';
import type { Transition } from 'lumen/services/transition';
import { getPeriodsInRange } from 'lumen/utils/dates/moment-helpers';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

type EventsRouteParams = {
  month: string;
};

type EventsRouteModel = {
  demand: Partial<TaskInstance<Demand[]>>;
  events: Partial<TaskInstance<Day[]>>;
  holidays: Partial<TaskInstance<Day[]>>;
};

export default class HotelEventsRoute extends Route {
  queryParams = {
    month: {
      refreshModel: true,
    },
    highlight: {
      refreshModel: false,
    },
  };

  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare navigation: Services['navigation'];
  @service('repositories/day')
  declare repositoriesDay: Services['repositories/day'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  modelObject = trackedObject<EventsRouteModel>({
    demand: {
      isError: false,
      isRunning: false,
    },
    events: {
      isError: false,
      isRunning: false,
      value: [] as Day[] | null,
    },
    holidays: {
      isError: false,
      isRunning: false,
    },
  });

  fetchDemandTask = task(
    { restartable: true },
    async ({
      formattedDateRangeStart,
      formattedDateRangeEnd,
    }: {
      formattedDateRangeEnd: string;
      formattedDateRangeStart: string;
    }) => {
      try {
        return await this.repositoriesDay.findDemandByDateRange({
          hotelId: (this.modelFor('hotel') as Hotel).id!,
          startDate: formattedDateRangeStart,
          endDate: formattedDateRangeEnd,
          typeKey: this.access.hasMarketInsight
            ? 'demand/di/demands'
            : 'demand/ari/demands',
        });
      } catch (error) {
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchEventsTask = task(
    { restartable: true },
    async ({
      formattedDateRangeStart,
      formattedDateRangeEnd,
      startUnix,
      endUnix,
    }: {
      endUnix: number;
      formattedDateRangeEnd: string;
      formattedDateRangeStart: string;
      startUnix: number;
    }) => {
      try {
        const days = await this.repositoriesDay.findEventsByDateRange({
          hotelId: (this.modelFor('hotel') as Hotel).id!,
          startDate: formattedDateRangeStart,
          endDate: formattedDateRangeEnd,
        });

        return days.filter(
          (x) => x.integerDate >= startUnix && x.integerDate <= endUnix,
        );
      } catch (error) {
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchHolidaysTask = task(
    { restartable: true },
    async ({
      formattedDateRangeStart,
      formattedDateRangeEnd,
    }: {
      formattedDateRangeEnd: string;
      formattedDateRangeStart: string;
    }) => {
      try {
        return this.repositoriesDay.findHolidaysByDateRange({
          hotelId: (this.modelFor('hotel') as Hotel).id!,
          startDate: formattedDateRangeStart,
          endDate: formattedDateRangeEnd,
        });
      } catch (error) {
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  beforeModel(transition: Transition): void {
    const isInternalTransition =
      transition.from?.name.startsWith('hotel.events');
    if (
      !isInternalTransition &&
      this.navigation.redirectToAccountSelection(transition)
    ) {
      return;
    }

    if (!this.access.hasEvents) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateEventsQueryParams(
        transition,
        this.paramsFor(this.routeName), // Use paramsFor to obtain the deserialised query params
      );
    }
  }

  deactivate(): void {
    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }
    this.store.unloadAll('event');
    this.store.unloadAll('holiday');
    this.store.unloadAll('demand');
  }

  private getDateRangeParams(month: string): {
    dateRangeEnd: ReturnType<typeof dayjs>;
    dateRangeStart: ReturnType<typeof dayjs>;
    endUnix: number;
    formattedDateRangeEnd: string;
    formattedDateRangeStart: string;
    startUnix: number;
  } {
    const monthMoment = this.hotelAndMarketService.toLocalTime(`${month}-15`);
    const dateRangeStart = monthMoment.startOf('month').startOf('week');
    const dateRangeEnd = monthMoment
      .endOf('month')
      .endOf('week')
      .startOf('day');
    const formattedDateRangeStart = dateRangeStart.format('YYYY-MM-DD');
    const formattedDateRangeEnd = dateRangeEnd.format('YYYY-MM-DD');
    const startUnix = Math.floor(
      dayjs.utc(formattedDateRangeStart).unix() / 86400,
    );
    const endUnix = Math.floor(dayjs.utc(formattedDateRangeEnd).unix() / 86400);

    return {
      dateRangeEnd,
      dateRangeStart,
      formattedDateRangeEnd,
      formattedDateRangeStart,
      startUnix,
      endUnix,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: EventsRouteParams) {
    const {
      dateRangeEnd,
      dateRangeStart,
      formattedDateRangeStart,
      formattedDateRangeEnd,
      startUnix,
      endUnix,
    } = this.getDateRangeParams(params.month);

    // Pre-create dummy Day records before the task starts so they already exist
    // when the loading state renders. This avoids calling store.createRecord()
    // inside a reactive getter (which causes a Glimmer autotracking violation).
    getPeriodsInRange(dateRangeStart, dateRangeEnd, 'day').forEach((period) => {
      const id = period.format();
      if (!this.repositoriesDay.peekByDate(id)) {
        this.repositoriesDay.add({ id, date: period });
      }
    });

    this.modelObject.events = this.fetchEventsTask.perform({
      formattedDateRangeStart,
      formattedDateRangeEnd,
      startUnix,
      endUnix,
    });

    this.modelObject.holidays = this.fetchHolidaysTask.perform({
      formattedDateRangeStart,
      formattedDateRangeEnd,
    });

    if (this.access.hasFeature('subscription.demand')) {
      this.modelObject.demand = this.fetchDemandTask.perform({
        formattedDateRangeStart,
        formattedDateRangeEnd,
      });
    }

    return this.modelObject;
  }

  refetchEvents(): void {
    const params = this.paramsFor(this.routeName) as EventsRouteParams;
    const {
      formattedDateRangeStart,
      formattedDateRangeEnd,
      startUnix,
      endUnix,
    } = this.getDateRangeParams(params.month);

    this.modelObject.events = this.fetchEventsTask.perform({
      formattedDateRangeStart,
      formattedDateRangeEnd,
      startUnix,
      endUnix,
    });
  }
}
